import { useCallback, useState } from 'react';
import { getConfig } from '@/config';
import { getRequestLocale } from '@/i18n/request-locale';

interface CheckoutResponse {
  checkout_url: string;
}

export interface UseStripeCheckoutResult {
  /** The priceId currently being checked out, or null when idle. */
  loadingPriceId: string | null;
  error: string | null;
  initiateCheckout: (priceId: string) => Promise<void>;
}

/** Initiates a Stripe Checkout Session and redirects to the Stripe-hosted page. */
export function useStripeCheckout(): UseStripeCheckoutResult {
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const initiateCheckout = useCallback(async (priceId: string) => {
    if (!priceId) {
      setError('A product must be selected to checkout.');
      return;
    }

    setLoadingPriceId(priceId);
    setError(null);

    try {
      const { apiBaseUrl } = getConfig();

      const response = await fetch(`${apiBaseUrl}/stripe/checkout`, {
        method: 'POST',
        // This bypasses Apollo, so x-locale must be set explicitly — it drives
        // the language of the Stripe-hosted checkout page.
        headers: { 'Content-Type': 'application/json', 'x-locale': getRequestLocale() },
        body: JSON.stringify({ priceId }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        const message = errorBody?.message || `Server returned ${response.status}`;
        throw new Error(message);
      }

      const data: CheckoutResponse = await response.json();

      // Hard-redirect to Stripe hosted checkout — intentionally leaves the SPA.
      window.location.href = data.checkout_url;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      setLoadingPriceId(null);
    }
    // No setLoadingPriceId(null) on success — browser is already navigating away.
  }, []);

  return { loadingPriceId, error, initiateCheckout };
}
